import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, CalendarDays, MapPin, Phone, Wallet, Users, FileText, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import { formatINR, formatDate } from '../lib/format';

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [reminders, setReminders] = useState([]);
  useEffect(() => {
    api.get('/dashboard').then(r => setD(r.data));
    api.get('/reminders').then(r => setReminders(r.data)).catch(() => setReminders([]));
  }, []);
  if (!d) return <div className="p-6 text-sm text-[var(--muted)]">Loading…</div>;

  const Card = ({ title, children, testId, action }) => (
    <section className="card-cream p-5 mb-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">{title}</div>
        {action}
      </div>
      {children}
    </section>
  );

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="serif text-[32px] leading-tight" style={{color:'var(--charcoal)'}}>Good day, Rishika</div>
        <div className="text-sm text-[var(--muted)]">{formatDate(new Date().toISOString())}</div>
      </div>

      {reminders.length > 0 && (
        <Card title="Reminders" testId="dash-reminders">
          {reminders.slice(0, 8).map((r, i) => (
            <div key={i} className="flex items-start gap-3 py-2 border-b hairline last:border-0">
              <span className="text-[10px] tracking-[0.14em] uppercase" style={{color: r.type==='balance_day_before' || r.type==='unpaid_after_event' ? 'var(--overdue)' : r.type==='birthday' || r.type==='anniversary' ? 'var(--gold)' : 'var(--pending)'}}>{r.type.replace(/_/g,' ')}</span>
              <div className="text-sm flex-1">{r.message}</div>
            </div>
          ))}
        </Card>
      )}

      {/* Alerts row */}
      {(d.unanswered.length > 0 || d.unconfirmed_crew.length > 0) && (
        <Card title="Attention" testId="dash-alerts">
          {d.unanswered.length > 0 && (
            <div className="flex items-start gap-3 py-2">
              <AlertTriangle size={16} style={{color:'var(--overdue)'}} className="mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">{d.unanswered.length} enquir{d.unanswered.length===1?'y':'ies'} unanswered over 24 hours</div>
                <Link to="/enquiries" className="text-xs text-[var(--gold)]" data-testid="dash-unanswered-link">Open enquiries →</Link>
              </div>
            </div>
          )}
          {d.unconfirmed_crew.length > 0 && (
            <div className="flex items-start gap-3 py-2">
              <AlertTriangle size={16} style={{color:'var(--pending)'}} className="mt-0.5" />
              <div className="text-sm">
                <div className="font-medium">{d.unconfirmed_crew.length} event{d.unconfirmed_crew.length===1?'':'s'} with unconfirmed crew</div>
                <div className="text-xs text-[var(--muted)]">Book hair artists and drapers for upcoming dates.</div>
              </div>
            </div>
          )}
        </Card>
      )}

      <Card title="Next 7 Days" testId="dash-upcoming">
        {d.upcoming.length === 0 ? (
          <div className="text-sm text-[var(--muted)] italic py-4">No events scheduled this week.</div>
        ) : (
          <div className="space-y-3">
            {d.upcoming.map((ev, idx) => (
              <div key={idx} className="flex items-start justify-between gap-3 py-2 border-b hairline last:border-0">
                <div>
                  <div className="serif text-[18px]">{ev.name} — {ev.client_name || ev.project_name}</div>
                  <div className="text-sm text-[var(--muted)] flex items-center gap-3 mt-1 flex-wrap">
                    <span className="flex items-center gap-1"><CalendarDays size={12} />{formatDate(ev.date)}</span>
                    {ev.reporting_time && <span className="flex items-center gap-1"><Clock size={12} />Report {ev.reporting_time}</span>}
                    {ev.venue_name && <span className="flex items-center gap-1"><MapPin size={12} />{ev.venue_name}</span>}
                  </div>
                </div>
                <Link to={`/projects/${ev.project_id}`} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase whitespace-nowrap">View</Link>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Money — Outstanding" testId="dash-money">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={16} style={{color:'var(--gold)'}} />
            <span className="serif text-[26px] mono-num">{formatINR(d.outstanding)}</span>
          </div>
          <div className="text-xs text-[var(--muted)]">Total client balances due across all invoices</div>
          {d.overdue_invoices.length > 0 && <div className="text-xs mt-2" style={{color:'var(--overdue)'}}>{d.overdue_invoices.length} overdue</div>}
        </Card>

        <Card title="This Month" testId="dash-month">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="eyebrow">Booked</div>
              <div className="serif text-[22px] mono-num">{formatINR(d.month.booked)}</div>
            </div>
            <div>
              <div className="eyebrow">Collected</div>
              <div className="serif text-[22px] mono-num" style={{color:'var(--ok)'}}>{formatINR(d.month.collected)}</div>
            </div>
          </div>
        </Card>

        <Card title="Artist Payouts Pending" testId="dash-payouts">
          <div className="flex items-center gap-2">
            <Users size={16} style={{color:'var(--gold)'}} />
            <span className="serif text-[22px]">{d.unpaid_artists.length}</span>
          </div>
          <Link to="/payouts" className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase mt-2 inline-block" data-testid="dash-payouts-link">Open payouts →</Link>
        </Card>

        <Card title="Follow-ups Due Today" testId="dash-followups">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} style={{color:'var(--gold)'}} />
            <span className="serif text-[22px]">{d.follow_ups.length}</span>
          </div>
          <Link to="/enquiries" className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase mt-2 inline-block">Open enquiries →</Link>
        </Card>
      </div>
    </div>
  );
}
